import React from 'react';
import { ReservationWithDetails, ReservationStatus } from '@/domain/entities';

interface ReservationsTableProps {
  reservations: ReservationWithDetails[];
}

const STATUSES: { value: ReservationStatus; label: string; color: string }[] = [
  { value: 'active', label: 'Активные', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  { value: 'completed', label: 'Завершенные', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  { value: 'cancelled', label: 'Отмененные', color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' },
];

export const ReservationsTable: React.FC<ReservationsTableProps> = ({
  reservations,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: ReservationStatus) => {
    const statusConfig = STATUSES.find(s => s.value === status);
    if (!statusConfig) return null;

    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded ${statusConfig.color}`}>
        {statusConfig.label}
      </span>
    );
  };

  if (!reservations || reservations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow ring-1 ring-black ring-opacity-5 dark:ring-gray-700 rounded-lg p-8 text-center text-gray-500 dark:text-gray-400">
        Резервы не найдены
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
            <tr>
              <th className="py-2.5 px-4 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Дата резерва
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Заказ
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Товар
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                SKU / Размер
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Количество
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Источник
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-r border-gray-300 dark:border-gray-600">
                Статус
              </th>
              <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider border-b border-gray-300 dark:border-gray-600">
                Заметки
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800">
            {reservations.map((reservation) => (
              <tr key={reservation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                <td className="whitespace-nowrap py-2.5 px-4 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                  {formatDate(reservation.reservedAt)}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {reservation.orderNumber}
                </td>
                <td className="py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {reservation.productName}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  <div>{reservation.sku}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">US {reservation.usSize}</div>
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm font-semibold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700">
                  {reservation.quantity}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm text-gray-500 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700">
                  {reservation.sourceName}
                </td>
                <td className="whitespace-nowrap py-2.5 px-3 text-sm border-r border-gray-200 dark:border-gray-700">
                  {getStatusBadge(reservation.status)}
                </td>
                <td className="py-2.5 px-3 text-sm text-gray-500 dark:text-gray-400">
                  {reservation.notes || '-'}
                  {reservation.adminName && (
                    <div className="text-xs mt-1">
                      Администратор: {reservation.adminName}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

